<template>
    <div class="mead-app-sidebar f"
        :class="[
            UIMobile ? 'el--mobile fv' : '',
            UIMobile && show_menu ? 'el--mobile-open' : '',
            !UIMobile && isCollapsed ? 'el--collapsed' : '']">
        <!-- Mobile top bar -->
        <div v-if="UIMobile" class="mead-app-sidebar-topbar f fh fa-c sp-l-m sp-r-m">
            <slot name="logo"></slot>
            <div class="fg"></div>
            <slot name="mobile-actions"></slot>
            <mead-button-icon :icon="show_menu ? 'cross2' : 'menu'"
                class="mead-app-sidebar-menu-toggle el--transparent sm-l-s"
                qa="app-sidebar-mobile-toggle"
                :title="show_menu ? 'mead.close_menu' : 'mead.open_menu'"
                :aria-expanded="String(show_menu)"
                @click="show_menu = !show_menu" />
        </div>
        <!-- Kept mounted until the sidebar finishes shrinking, so closing doesn't flash the rail color -->
        <transition :duration="{enter: 0, leave: uiMenuLeaveDuration}">
            <AppSidebarMobileMenu v-if="UIMobile && show_menu"
                class="fg"
                :items="allItems"
                :selectedItem="selectedItem"
                @select="onSelectMobile"
                @upsell="onUpsell">
                <template v-if="$scopedSlots.footer" #footer>
                    <slot name="footer"></slot>
                </template>
            </AppSidebarMobileMenu>
        </transition>

        <!-- Desktop icon rail -->
        <div v-if="!UIMobile"
            class="mead-app-sidebar-rail f fv fa-c sp-t-m sp-b-m"
            :class="[hovering_expand_area && canExpand ? 'el--expand-hover' : '']">
            <slot name="logo"></slot>
            <nav class="f fv fa-c dw-10 mead-app-sidebar-rail-items sm-t-m sp-t-xs" :aria-label="uiNavigationName">
                <AppSidebarRailItem v-for="item in items"
                    :key="item.uid"
                    :item="item"
                    :active="selectedItem !== null && selectedItem.uid === item.uid"
                    :hovered="hovered_uid === item.uid"
                    @click="onClickRailItem"
                    @hover="onHoverRailItem($event, item)" />
            </nav>
            <div class="mead-app-sidebar-expand-area f fg fa-c fj-c dw-10"
                qa="app-sidebar-expand-area"
                :class="[canExpand ? 'c-p' : '']"
                @click="toggleCollapsed"
                @mouseenter="hovering_expand_area = true"
                @mouseleave="hovering_expand_area = false">
                <mead-icon v-if="canExpand && hovering_expand_area"
                    :name="isCollapsed ? 'enter-right2' : 'enter-left2'"
                    uiSize="xl" />
            </div>
            <nav v-if="bottomItems.length > 0"
                class="f fv fa-c"
                :aria-label="uiNavigationName">
                <AppSidebarRailItem v-for="item in bottomItems"
                    :key="item.uid"
                    :item="item"
                    :active="selectedItem !== null && selectedItem.uid === item.uid"
                    :hovered="hovered_uid === item.uid"
                    @click="onClickRailItem"
                    @hover="onHoverRailItem($event, item)" />
            </nav>
            <div v-if="$scopedSlots.footer" class="mead-app-sidebar-footer f fv fa-c sm-t-s">
                <slot name="footer"></slot>
            </div>
        </div>

        <!-- Desktop sub-panel -->
        <div v-if="!UIMobile && !isCollapsed"
            class="mead-app-sidebar-panel f fv b-r"
            qa="app-sidebar-panel">
            <div class="mead-app-sidebar-panel-header f fa-c sp-m b-b">
                <mead-icon v-if="selectedItem.icon" :name="selectedItem.icon" uiSize="l" class="sm-r-s" />
                <div class="te-fat-section">{{ uiSelectedLabel }}</div>
            </div>
            <nav class="mead-app-sidebar-panel-items fg sp-t-m sp-l-m sp-r-m" :aria-label="uiSelectedLabel">
                <AppSidebarItem v-for="child in selectedItem.children"
                    :key="child.uid"
                    :item="child"
                    @select="onSelect($event, selectedItem)"
                    @upsell="onUpsell" />
            </nav>
        </div>
    </div>
</template>
<script>
import UIMobileMixin from '../../../mixins/UIMobile';
import AppSidebarItem from './AppSidebarItem.vue';
import AppSidebarItemPopover from './AppSidebarItemPopover.vue';
import AppSidebarMobileMenu from './AppSidebarMobileMenu.vue';
import AppSidebarRailItem from './AppSidebarRailItem.vue';

export default {
    name: 'mead-app-sidebar',
    //  Imported rather than read from the Mead global, which doesn't exist yet when Mead loads this file
    mixins: [UIMobileMixin],
    components: {
        AppSidebarItem,
        AppSidebarMobileMenu,
        AppSidebarRailItem,
    },
    props: {
        /**
         * `MeadCommon.AppSidebarItem`s on the main icon rail. Nest items with `setChildren()` (any
         * depth), and mark the item for the current page with `setActive(true)`; its top level section
         * becomes the selected one. `setUpsell(true)` makes a click emit `upsell` instead of `select`.
         * Keep uids stable when rebuilding items (`setUid()`), they decide which section stays selected.
         */
        items: {type: Array, default: () => []},
        /**
         * `MeadCommon.AppSidebarItem`s pinned to the bottom of the rail, below the expand area.
         */
        bottomItems: {type: Array, default: () => []},
        /**
         * Whether the user has collapsed the sub-panel. Use with `.sync` and persist it if needed;
         * it only changes when the user toggles the panel. The panel is also hidden whenever the
         * selected section has no children.
         */
        collapsed: {type: Boolean, default: false},
    },
    data: function () {
        return {
            is_collapsed: this.collapsed,
            // uid of the last top level item clicked, used when no item is active
            selected_uid: null,
            hovered_uid: null,
            hovering_expand_area: false,
            show_menu: false,
        };
    },
    methods: {
        toggleCollapsed: function () {
            if (!this.canExpand) return;
            this.is_collapsed = !this.isCollapsed;
            this.$emit('update:collapsed', this.is_collapsed);
        },
        onClickRailItem: function (item) {
            if (item.disabled) return;
            MeadCommon.PopoverService.close();

            if (item.upsell) {
                this.onUpsell(item);
            } else {
                this.onSelect(item, item);
            }
        },
        onHoverRailItem: function (event, item) {
            if (this.hovered_uid === item.uid) return;
            this.hovered_uid = item.uid;

            MeadCommon.PopoverService.close();
            MeadCommon.PopoverService.openPopover(
                new MeadCommon.PopoverConfig()
                    .setComponent(AppSidebarItemPopover)
                    .setEvent(event)
                    .setPlacement('right-start')
                    .setHideOnMouseLeave(true)
                    .setStopPropagationOnClose(false)
                    .setProps({
                        item,
                        showChildren: this.isCollapsed || this.selectedItem?.uid !== item.uid,
                        onSelect: child => this.onSelect(child, item),
                        onUpsell: child => this.onUpsell(child),
                    })
                    .setNext(() => {
                        if (this.hovered_uid === item.uid) this.hovered_uid = null;
                    })
            );
        },
        onSelectMobile: function (item, section) {
            this.show_menu = false;
            this.onSelect(item, section);
        },
        onSelect: function (item, section) {
            this.selected_uid = section.uid;
            this.$emit('select', item);
        },
        onUpsell: function (item) {
            this.show_menu = false;
            this.$emit('upsell', item);
        },
    },
    computed: {
        allItems: function () {
            return [...this.items, ...this.bottomItems];
        },
        /**
         * The section shown in the sub-panel: the first top level item that is active, itself or
         * through a descendant, otherwise the last one clicked.
         */
        selectedItem: function () {
            return this.allItems.find(item => item.isActive)
                || this.allItems.find(item => item.uid === this.selected_uid)
                || null;
        },
        canExpand: function () {
            return this.selectedItem !== null && !this.selectedItem.upsell && this.selectedItem.hasChildren;
        },
        isCollapsed: function () {
            return this.is_collapsed || !this.canExpand;
        },
        uiSelectedLabel: function () {
            return this.selectedItem ? MeadCommon.I18N.go(this.selectedItem.label) : '';
        },
        /**
         * Matches the sidebar's height transition, none when the user prefers reduced motion
         */
        uiMenuLeaveDuration: function () {
            return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : 200;
        },
        uiNavigationName: function () {
            return MeadCommon.I18N.go('mead.main_navigation');
        },
    },
    watch: {
        collapsed: function (value) {
            this.is_collapsed = value;
        },
    },
};
</script>

<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.mead-app-sidebar {
    position: relative;
    height: 100%;
    z-index: $zx-nav-primary;

    .mead-app-sidebar-rail {
        width: 7rem;
        height: 100%;
        flex-shrink: 0;
        background-color: var(--c-sidebar-rail-bg);
        color: var(--c-sidebar-rail-fg);
        transition: background-color .2s ease;

        &.el--expand-hover {
            background-color: var(--c-sidebar-rail-bg-hover);
        }
    }

    //  Full rail width so item badges, which sit past the icon's edge, don't overflow sideways
    .mead-app-sidebar-rail-items {
        overflow-x: hidden;
        overflow-y: auto;
    }

    .mead-app-sidebar-expand-area {
        min-height: 4rem;
    }

    .mead-app-sidebar-panel {
        width: 25rem;
        height: 100%;
        flex-shrink: 0;
        background-color: var(--c-sidebar-panel-bg);
        color: var(--c-sidebar-panel-fg);

        .mead-app-sidebar-panel-header {
            min-height: 7rem;
        }

        .mead-app-sidebar-panel-items {
            min-height: 0;
            overflow-y: auto;
        }
    }

    //  Mobile: a top bar that opens into a full height menu
    &.el--mobile {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: $d-h-nav;
        overflow: hidden;
        background-color: var(--c-sidebar-rail-bg);
        color: var(--c-sidebar-rail-fg);
        transition: height .2s ease;

        &.el--mobile-open {
            height: 100%;
        }

        .mead-app-sidebar-topbar {
            height: $d-h-nav;
            flex-shrink: 0;
        }

        .mead-app-sidebar-menu-toggle {
            color: var(--c-sidebar-rail-fg);
        }
    }

    @media (prefers-reduced-motion: reduce) {
        &,
        .mead-app-sidebar-rail {
            transition: none !important;
        }
    }

}

html[printmode] .mead-app-sidebar {
    display: none;
}

@media print {
    .mead-app-sidebar {
        display: none;
    }
}
</style>
